// utils/authHelpers.ts - Centralized authentication helpers

import { EnhancedTokenManager } from './tokenManager';
import { SubdomainManager } from './subdomainUtils';
import { API_URLS } from '../api/urls';

/**
 * ✅ Centralized logout function that always redirects to app domain
 * Use this everywhere you need to log out the user
 */
export const handleUserLogout = async (): Promise<void> => {
  console.log("🚪 Initiating user logout");
  
  // ✅ STEP 1: Set logout flag IMMEDIATELY to prevent token restoration
  sessionStorage.setItem('logout_in_progress', 'true');
  console.log("✅ Logout flag set");
  
  // ✅ STEP 2: Clear tokens synchronously (before any async operations)
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("current_user_id");
  EnhancedTokenManager.clearDeveloperCredentials();
  delete (window as any).__feebdack_tokens;
  delete (window as any).__current_user_email;
  
  console.log("✅ Tokens cleared immediately");
  
  // ✅ STEP 3: Dispatch logout event
  window.dispatchEvent(new Event('user_logout'));
  
  // ✅ STEP 4: Clear blocker
  sessionStorage.removeItem('nav_blocker_active');
  
  // ✅ STEP 5: Try API logout (non-blocking, fire and forget)
  const accessToken = localStorage.getItem("access_token_backup"); // We might have a backup
  if (accessToken) {
    fetch(API_URLS.LOGOUT, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    }).catch(err => console.warn("API logout failed:", err));
  }
  
  // ✅ STEP 6: Determine redirect destination
  const isOnSubdomain = SubdomainManager.isOnWorkspaceSubdomain();
  const isLocal = SubdomainManager.isLocalDevelopment();
  
  // ✅ STEP 7: Clear workspace subdomain
  SubdomainManager.clearWorkspaceSubdomain();
  
  // ✅ STEP 8: Redirect appropriately
  if (isOnSubdomain && !isLocal) {
    // On subdomain: navigate to app domain with clearTokens
    console.log("🔄 Logging out from subdomain, redirecting to app.cluva.io");
    SubdomainManager.navigateToAppDomain('/', true);
  } else {
    // On app domain or localhost: reload to clear state
    console.log("🔄 Logging out from app domain, reloading");
    window.location.href = '/?logout=true';
  }
};

/**
 * ✅ Quick logout for immediate UI feedback
 * Clears tokens instantly, then handles redirect
 */
export const quickLogout = (): void => {
  console.log("⚡ Quick logout triggered");
  
  // Set flag immediately
  sessionStorage.setItem('logout_in_progress', 'true');
  
  // Clear tokens
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("current_user_id");
  
  // Dispatch event
  window.dispatchEvent(new Event('user_logout'));
  
  // Clear subdomain
  SubdomainManager.clearWorkspaceSubdomain();
  
  // Redirect based on location
  const isOnSubdomain = SubdomainManager.isOnWorkspaceSubdomain();
  const isLocal = SubdomainManager.isLocalDevelopment();
  
  if (isOnSubdomain && !isLocal) {
    SubdomainManager.navigateToAppDomain('/', true);
  } else {
    window.location.href = '/?logout=true';
  }
};

/**
 * ✅ Check if user is authenticated (use this instead of manual checks)
 */
export const isUserAuthenticated = (): boolean => {
  // Check logout flag first
  const logoutInProgress = sessionStorage.getItem('logout_in_progress');
  if (logoutInProgress === 'true') {
    return false;
  }
  
  const accessToken = EnhancedTokenManager.getAccessToken();
  const refreshToken = EnhancedTokenManager.getRefreshToken();
  return !!(accessToken && refreshToken);
};

/**
 * ✅ Get current authentication state
 */
export const getAuthState = () => {
  return {
    isAuthenticated: isUserAuthenticated(),
    isOnSubdomain: SubdomainManager.isOnWorkspaceSubdomain(),
    currentSubdomain: SubdomainManager.getCurrentSubdomain(),
    userProfile: EnhancedTokenManager.getUserProfile(),
    workspaceId: EnhancedTokenManager.getWorkspaceIdForCurrentUser(),
    logoutInProgress: sessionStorage.getItem('logout_in_progress') === 'true',
  };
};

/**
 * ✅ Clear logout flag (call after successful navigation)
 */
export const clearLogoutFlag = (): void => {
  sessionStorage.removeItem('logout_in_progress');
  console.log("✅ Logout flag cleared");
};

/**
 * ✅ Complete data wipe (for switching accounts)
 */
export const completeLogout = (): void => {
  console.log("🧹 Complete logout - clearing all data");
  
  sessionStorage.setItem('logout_in_progress', 'true');
  
  // Clear all storage
  EnhancedTokenManager.clearAllData();
  SubdomainManager.clearWorkspaceSubdomain();
  
  // Dispatch event
  window.dispatchEvent(new Event('user_logout'));
  
  // Redirect
  const isOnSubdomain = SubdomainManager.isOnWorkspaceSubdomain();
  const isLocal = SubdomainManager.isLocalDevelopment();
  
  if (isOnSubdomain && !isLocal) {
    SubdomainManager.navigateToAppDomain('/', true);
  } else {
    window.location.href = '/?logout=true';
  }
};